import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, RefreshCw } from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import BannerImg from "../image/gallaryBanner.png";

const MentorMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: "", message: "" });
  const fetchedRef = useRef(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setNotice({ type: "", message: "" });
    try {
      const { data } = await axios.get("/api/mentor/my-messages", { withCredentials: true });
      if (data.success) setMessages(data.messages || []);
    } catch (err) {
      setNotice({ type: "error", message: err?.response?.data?.message || "Could not load messages." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchMessages();
    }
  }, [fetchMessages]);

  return (
    <div>
      <Header />
      <Banner backgroundImage={BannerImg} heading="Mentor Messages" minHeight="40vh" padding="80px 0" />

      <section className="main-lwbl rounded-[30px] bg-[#F9FAFB] px-4 py-16">
        <div className="mx-auto max-w-3xl">

          <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                <MessageSquare size={24} />
              </div>
              <div>
                <p className="font-outfit text-sm font-semibold uppercase tracking-[0.18em] text-cyan-600">
                  From your mentor
                </p>
                <h2 className="mt-1 font-cinzel text-2xl font-bold text-gray-900">Mentor Messages</h2>
                <p className="mt-1 font-outfit text-sm text-gray-500">
                  Messages sent to you by your assigned mentor.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchMessages}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-outfit text-sm font-semibold text-gray-700 hover:border-cyan-300 hover:text-cyan-700"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
          </div>

          {notice.message && (
            <div className={`mb-5 rounded-2xl border px-4 py-3 font-outfit text-sm ${
              notice.type === "error"
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}>
              {notice.message}
            </div>
          )}

          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-cyan-400 border-t-transparent" />
              </div>
            ) : messages.length === 0 ? (
              <div className="px-6 py-20 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-300">
                  <MessageSquare size={30} />
                </div>
                <h3 className="font-cinzel text-xl font-bold text-gray-800">No messages yet</h3>
                <p className="mx-auto mt-2 max-w-sm font-outfit text-sm text-gray-500">
                  Messages from your mentor will appear here.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {messages.map((msg) => (
                  <div key={msg._id} className="px-6 py-5">
                    <p className="font-outfit text-sm leading-6 text-gray-700">{msg.message}</p>
                    <p className="mt-2 font-outfit text-xs text-gray-400">
                      {new Date(msg.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MentorMessages;
